import { useCallback, useEffect, useRef, useState } from 'react';

import { DomEventEnum, IStickyToolbarState, IUseStickyToolbarOptions } from '../types/shell';

const INITIAL_STATE: IStickyToolbarState = {
  isStuck: false,
  isCondensed: false,
};

export const useStickyToolbar = ({
  sentinelRef,
  stickyTop,
  condenseAfter,
  isSearchFocused = false,
}: IUseStickyToolbarOptions): IStickyToolbarState => {
  const [state, setState] = useState<IStickyToolbarState>(INITIAL_STATE);

  const stateRef = useRef<IStickyToolbarState>(INITIAL_STATE);
  const stickyTopRef = useRef(stickyTop);
  stickyTopRef.current = stickyTop;
  const condenseAfterRef = useRef(condenseAfter);
  condenseAfterRef.current = condenseAfter;
  const isSearchFocusedRef = useRef(isSearchFocused);
  isSearchFocusedRef.current = isSearchFocused;

  const evaluate = useCallback(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel) {
      return;
    }

    const travel = stickyTopRef.current - sentinel.getBoundingClientRect().top;
    const isStuck = travel >= 0;
    const threshold = condenseAfterRef.current;
    const isCondensed = threshold !== undefined && !isSearchFocusedRef.current && travel >= threshold;

    if (isStuck !== stateRef.current.isStuck || isCondensed !== stateRef.current.isCondensed) {
      stateRef.current = { isStuck, isCondensed };
      setState(stateRef.current);
    }
  }, [sentinelRef]);

  useEffect(() => {
    evaluate();
    window.addEventListener(DomEventEnum.Scroll, evaluate, { passive: true, capture: true });
    window.addEventListener(DomEventEnum.Resize, evaluate);

    return () => {
      window.removeEventListener(DomEventEnum.Scroll, evaluate, { capture: true });
      window.removeEventListener(DomEventEnum.Resize, evaluate);
    };
  }, [evaluate]);

  useEffect(() => {
    evaluate();
  }, [isSearchFocused, condenseAfter, stickyTop, evaluate]);

  return state;
};
